import React from "react";

import {
    FaBell,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
} from "react-icons/fa";

import SectionCard from "./SectionCard";

import {
    formatCurrency,
    formatNumber,
} from "../utils/dashboardFormatters";


/*
|--------------------------------------------------------------------------
| ALERT CENTER
|--------------------------------------------------------------------------
|
| Expected alert object:
|
| {
|     type: "TASK_OVERDUE",
|     severity: "danger",
|     title: "Overdue Tasks",
|     message: "3 task(s) are overdue.",
|     count: 3
| }
|
| OR
|
| {
|     type: "OUTSTANDING_PAYMENT",
|     severity: "warning",
|     title: "Outstanding Payment",
|     message: "Customer payment is still outstanding.",
|     amount: 25000
| }
|
|--------------------------------------------------------------------------
*/

const AlertCenter = ({
    alerts = [],
}) => {

    /*
    |--------------------------------------------------------------------------
    | ICON BY SEVERITY
    |--------------------------------------------------------------------------
    */

    const getAlertIcon = (
        severity
    ) => {

        switch (
            severity
        ) {

            case "danger":
                return FaExclamationTriangle;


            case "warning":
                return FaBell;


            case "success":
                return FaCheckCircle;


            case "info":
            default:
                return FaInfoCircle;

        }

    };


    /*
    |--------------------------------------------------------------------------
    | FORMAT VALUE
    |--------------------------------------------------------------------------
    */

    const getAlertValue = (
        alert
    ) => {

        if (
            alert?.amount !==
            undefined &&
            alert?.amount !==
            null
        ) {

            return formatCurrency(
                alert.amount
            );

        }


        if (
            alert?.count !==
            undefined &&
            alert?.count !==
            null
        ) {

            return formatNumber(
                alert.count
            );

        }


        return "";

    };


    /*
    |--------------------------------------------------------------------------
    | EMPTY STATE
    |--------------------------------------------------------------------------
    */

    const renderEmptyState =
        () => {

            return (

                <div
                    className="dashboard-alert-empty"
                >

                    <div
                        className="dashboard-alert-empty-icon"
                    >

                        <FaCheckCircle />

                    </div>


                    <h4>
                        No urgent alerts
                    </h4>


                    <p>
                        Everything looks under control right now.
                    </p>

                </div>

            );

        };


    /*
    |--------------------------------------------------------------------------
    | ALERT LIST
    |--------------------------------------------------------------------------
    */

    const renderAlerts =
        () => {

            return (

                <div
                    className="dashboard-alert-list"
                >

                    {
                        alerts.map(
                            (
                                alert,
                                index
                            ) => {

                                const Icon =
                                    getAlertIcon(
                                        alert?.severity
                                    );


                                const severity =
                                    alert?.severity ||
                                    "info";


                                return (

                                    <div
                                        key={
                                            alert?.type
                                                ? `${alert.type}-${index}`
                                                : `dashboard-alert-${index}`
                                        }

                                        className={
                                            `dashboard-alert-item dashboard-alert-item--${severity}`
                                        }
                                    >

                                        {/* ICON */}

                                        <div
                                            className="dashboard-alert-icon"
                                        >

                                            <Icon />

                                        </div>


                                        {/* CONTENT */}

                                        <div
                                            className="dashboard-alert-content"
                                        >

                                            <strong>

                                                {
                                                    alert?.title ||
                                                    "Business Alert"
                                                }

                                            </strong>


                                            <p>

                                                {
                                                    alert?.message ||
                                                    "Action may be required."
                                                }

                                            </p>

                                        </div>


                                        {/* VALUE */}

                                        {
                                            getAlertValue(
                                                alert
                                            ) && (

                                                <div
                                                    className="dashboard-alert-value"
                                                >

                                                    {
                                                        getAlertValue(
                                                            alert
                                                        )
                                                    }

                                                </div>

                                            )
                                        }

                                    </div>

                                );

                            }
                        )
                    }

                </div>

            );

        };


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <SectionCard
            title="Action Center"
            subtitle="Important business items requiring attention."
            className="dashboard-alert-section"
        >

            {
                Array.isArray(
                    alerts
                ) &&
                alerts.length > 0
                    ? renderAlerts()
                    : renderEmptyState()
            }

        </SectionCard>

    );

};


export default AlertCenter;